<template>
  <div class="ticket-widget">
    <!-- Overlay do popup -->
    <div v-if="showPopup" class="popup-overlay" @click="closePopup">
      <div class="popup-content" @click.stop>
        <div class="popup-header">
          <button class="close-btn" @click="closePopup">&times;</button>
        </div>
        
        <!-- Configuração do UUID do Cliente -->
        <div v-if="!computedClientUuid" class="uuid-config-section">
          <!-- Título e subtítulo dentro do conteúdo -->
          <div class="content-header">
            <h3>Precisa de ajuda?</h3>
            <p>Fale com um de nossos especialistas</p>
          </div>
          <div class="config-header">
            <span class="config-icon">🔧</span>
            <div class="config-details">
              <div class="config-title">Configuração Necessária</div>
              <div class="config-subtitle">Configure o UUID do cliente para continuar</div>
            </div>
          </div>
          
          <div class="form-group">
            <label for="uuidInput">UUID do Cliente *</label>
            <input
              id="uuidInput"
              v-model="uuidInput"
              type="text"
              placeholder="Digite o UUID do cliente (ex: 1c9eb621-b56c-43e4-b04b-b1a91b0a1809)"
              class="form-input uuid-input"
              :style="inputFocusStyle"
              @keypress="handleUuidKeypress"
            />
          </div>
          
          <div v-if="uuidError" class="error-message">
            {{ uuidError }}
          </div>
          
          <div class="form-actions">
            <button type="button" @click="closePopup" class="btn btn-cancel">
              Cancelar
            </button>
            <button type="button" @click="setClientUuid" class="btn btn-submit" :style="submitButtonStyle">
              ✅ Configurar UUID
            </button>
          </div>
        </div>
        
        <!-- Formulário do ticket -->
        <form v-if="computedClientUuid" @submit.prevent="submitTicket" class="ticket-form">
          <!-- Título e subtítulo dentro do conteúdo -->
          <div class="content-header">
            <h3>Precisa de ajuda?</h3>
            <p>Fale com um de nossos especialistas</p>
          </div>
          
          <div class="form-group">
            <label for="title">Título *</label>
            <input
              id="title"
              v-model="ticket.title"
              type="text"
              required
              placeholder="Digite o título do ticket"
              class="form-input"
              :style="inputFocusStyle"
            />
          </div>
          
          <div class="form-group">
            <label for="description">Descrição *</label>
            <textarea
              id="description"
              v-model="ticket.description"
              required
              placeholder="Descreva o problema ou solicitação"
              class="form-textarea"
              :style="inputFocusStyle"
              rows="4"
            ></textarea>
          </div>

          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>
          
          <div class="form-actions">
            <button type="button" @click="closePopup" class="btn btn-cancel">
              Cancelar
            </button>
            <button type="submit" class="btn btn-submit" :disabled="isSubmitting" :style="submitButtonStyle">
              {{ isSubmitting ? 'Enviando...' : 'Criar Ticket' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TicketWidget',
  inject: {
    deepServiceDeskConfig: {
      from: 'deepServiceDeskConfig',
      default: () => ({})
    }
  },
  props: {
    clientUuid: {
      type: String,
      default: null
    },
    brandColor: {
      type: String,
      default: '#3b82f6'
    }
  },
  data() {
    return {
      showPopup: false,
      isSubmitting: false,
      userToken: null,
      userEmail: null,
      client: null,
      errorMessage: '',
      ticket: {
        title: '',
        description: '',
        type: 'support',
        priority: 'medium'
      },
      uuidInput: '',
      uuidError: null,
      configuredClientUuid: null
    }
  },
  computed: {
    // URL da API - sempre fixa
    computedApiUrl() {
      return 'https://servicedesk.deeprocket.com.br/api';
    },
    
    // Cores derivadas do brandColor
    computedBrandColor() {
      return this.brandColor || '#3b82f6';
    },
    
    computedBrandColorHover() {
      // Criar uma versão mais escura da cor para hover
      return this.darkenColor(this.computedBrandColor, 0.1);
    },
    
    computedBrandColorFocus() {
      // Criar uma versão com transparência para o foco
      return this.hexToRgba(this.computedBrandColor, 0.1);
    },
    
    // Estilos dinâmicos para os botões
    submitButtonStyle() {
      return {
        backgroundColor: this.computedBrandColor,
        borderColor: this.computedBrandColor
      };
    },
    
    inputFocusStyle() {
      return {
        '--brand-color': this.computedBrandColor,
        '--brand-color-focus': this.computedBrandColorFocus
      };
    },
    
    // UUID do cliente com prioridade: configurado manualmente > prop > config global > variável de ambiente
    computedClientUuid() {
      let clientUuid = this.configuredClientUuid || this.clientUuid || this.deepServiceDeskConfig.clientUuid;
      
      if (!clientUuid) {
        // Verificar variáveis de ambiente de forma segura
        if (typeof import.meta !== 'undefined' && import.meta.env) {
          clientUuid = import.meta.env.VITE_CLIENT_UUID;
        }
        
        if (!clientUuid && typeof window !== 'undefined') {
          clientUuid = window.DEEP_SERVICE_DESK_CLIENT_UUID;
        }
        
        if (!clientUuid && typeof process !== 'undefined' && process.env) {
          clientUuid = process.env.VUE_APP_CLIENT_UUID || process.env.VITE_CLIENT_UUID;
        }
      }
      
      return clientUuid || null;
    }
  },
  mounted() {
    // Escuta o evento 'new-ticket' para abrir o popup
    window.addEventListener('new-ticket', this.handleNewTicketEvent)
    
    // Tentar carregar UUID de variáveis de ambiente (opcional)
    this.loadClientUuidFromEnv()
  },
  beforeDestroy() {
    window.removeEventListener('new-ticket', this.handleNewTicketEvent)
  },
  methods: {
    // Função para obter token de autenticação do sistema externo
    getAuthTokenFromExternalSystem() {
      try {
        // Busca tokens em ordem de prioridade
        const tokenTc = localStorage.getItem('token-tc');
        if (tokenTc) return tokenTc;
        
        const tokenDeep = localStorage.getItem('token-deep');
        if (tokenDeep) return tokenDeep;
        
        const authToken = localStorage.getItem('authToken');
        if (authToken) return authToken;
        
        return null;
      } catch (error) {
        console.error('Erro ao acessar token:', error);
        return null;
      }
    },

    // Função para obter email do usuário do sistema externo
    getUserEmailFromExternalSystem() {
      try {
        // Busca o usuário do sistema externo (user-tc)
        const userTcToken = localStorage.getItem('user-tc');
        
        if (userTcToken) {
          try {
            const userData = JSON.parse(atob(userTcToken));
            return userData.email || null;
          } catch (decodeError) {
            // Silenciar erro de decodificação
          }
        }
        
        // Fallback para outros sistemas
        const userDeep = localStorage.getItem('user-deep');
        if (userDeep) {
          try {
            const userData = JSON.parse(atob(userDeep));
            return userData.email || null;
          } catch (decodeError) {
            // Silenciar erro de decodificação
          }
        }
        
        return null;
      } catch (error) {
        console.error('Erro ao acessar dados do usuário:', error);
        return null;
      }
    },

    // Função para buscar cliente pelo UUID
    async fetchClientByUuid() {
      if (!this.userToken || !this.computedClientUuid) return null;

      try {
        const response = await fetch(`${this.computedApiUrl}/clients/find-by-uuid?uuid=${encodeURIComponent(this.computedClientUuid)}`, {
          headers: {
            'Authorization': `Bearer ${this.userToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const client = await response.json();
          return client;
        } else if (response.status === 404) {
          return null;
        } else {
          return null;
        }
      } catch (error) {
        console.error('Erro ao buscar cliente por UUID:', error);
        return null;
      }
    },

    // Função para buscar cliente pelo email
    async findClientByEmail(email) {
      if (!email || !this.userToken) return null;

      try {
        const response = await fetch(`${this.computedApiUrl}/clients/find-by-email?email=${encodeURIComponent(email)}`, {
          headers: {
            'Authorization': `Bearer ${this.userToken}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const client = await response.json();
          return client;
        } else if (response.status === 404) {
          // Cliente não encontrado - isso é normal
          return null;
        } else {
          return null;
        }
      } catch (error) {
        console.error('Erro ao buscar cliente por email:', error);
        return null;
      }
    },

    // Função para buscar clientes
    async fetchClients() {
      if (!this.userToken) return

      try {
        const response = await fetch(`${this.computedApiUrl}/clients`, {
          headers: {
            'Authorization': `Bearer ${this.userToken}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const data = await response.json()
          this.clients = data.data || data
        }
      } catch (error) {
        console.error('Erro ao buscar clientes:', error)
      }
    },

    // Função para abrir o popup
    async openPopup() {
      // Se UUID não estiver configurado, apenas abre o popup para configuração
      if (!this.computedClientUuid) {
        this.errorMessage = '';
        this.showPopup = true;
        return;
      }
      
      // Se UUID já estiver configurado, inicializa o widget
      await this.initializeWidget();
      
      if (this.client) {
        this.errorMessage = '';
        this.showPopup = true;
      } else {
        this.errorMessage = 'Erro ao configurar widget. Verifique o UUID e autenticação.';
      }
    },

    // Função para fechar o popup
    closePopup() {
      this.showPopup = false
      this.resetForm()
      this.errorMessage = ''
    },

    // Função para resetar o formulário
    resetForm() {
      this.ticket.title = ''
      this.ticket.description = ''
      this.ticket.type = 'support'
      this.ticket.priority = 'medium'
    },

    // Função para submeter o ticket
    async submitTicket() {
      if (!this.userToken) {
        this.errorMessage = 'Token de autenticação não encontrado';
        return;
      }

      if (!this.client) {
        this.errorMessage = 'Cliente não configurado';
        return;
      }

      this.isSubmitting = true;
      this.errorMessage = '';

      try {
        const ticketData = {
          title: this.ticket.title.trim(),
          description: this.ticket.description.trim(),
          type: this.ticket.type,
          priority: this.ticket.priority,
          status: 'open',
          clientId: this.client.id
        };

        // Adicionar informações do usuário se disponível
        if (this.userEmail) {
          ticketData.description += `\n\n--- Informações do Usuário ---\nEmail: ${this.userEmail}`;
        }

        const response = await fetch(`${this.computedApiUrl}/tickets`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.userToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(ticketData)
        });

        const responseData = await response.json();

        if (response.ok) {
          // Sucesso - emite evento com dados do ticket criado
          this.$emit('ticket-created', {
            ...responseData,
            success: true,
            message: 'Ticket criado com sucesso!'
          });
          
          // Fecha o popup e reseta o form
          this.closePopup();
        } else {
          // Erro da API
          const errorMsg = responseData.error || 'Erro ao criar ticket';
          this.errorMessage = errorMsg;
          this.$emit('ticket-error', { 
            message: errorMsg,
            details: responseData
          });
        }
        
      } catch (error) {
        console.error('Erro ao criar ticket:', error);
        const errorMsg = 'Erro de conexão. Verifique sua internet e tente novamente.';
        this.errorMessage = errorMsg;
        this.$emit('ticket-error', { 
          message: errorMsg,
          details: error.message
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    // Handler para o evento customizado
    handleNewTicketEvent() {
      this.openPopup()
    },

    // Método público para verificar se está aberto (para uso via ref)
    isOpen() {
      return this.showPopup
    },

    // Método para carregar o UUID do cliente de variáveis de ambiente
    loadClientUuidFromEnv() {
      // Tentar carregar de diferentes fontes de variáveis de ambiente
      let envUuid = null;
      
      // Verificar se estamos em ambiente de desenvolvimento com Vite
      if (typeof import.meta !== 'undefined' && import.meta.env) {
        envUuid = import.meta.env.VITE_CLIENT_UUID;
      }
      
      // Verificar variável global do window
      if (!envUuid && typeof window !== 'undefined') {
        envUuid = window.DEEP_SERVICE_DESK_CLIENT_UUID;
      }
      
      // Verificar se process está disponível (apenas em desenvolvimento)
      if (!envUuid && typeof process !== 'undefined' && process.env) {
        envUuid = process.env.VUE_APP_CLIENT_UUID || process.env.VITE_CLIENT_UUID;
      }
      
      if (!envUuid) {
        return;
      }
      
      // Validação básica de UUID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(envUuid)) {
        console.error('❌ UUID do cliente inválido:', envUuid);
        return;
      }
      
      this.configuredClientUuid = envUuid;
      
      // Inicializar widget automaticamente se UUID for carregado de variável de ambiente
      this.initializeWidget();
    },

    handleUuidKeypress(event) {
      if (event.key === 'Enter') {
        this.setClientUuid();
      }
    },

    setClientUuid() {
      const uuid = this.uuidInput.trim();
      
      if (!uuid) {
        this.uuidError = 'Por favor, digite um UUID';
        return;
      }
      
      // Validação básica de UUID
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(uuid)) {
        this.uuidError = 'UUID inválido. Formato esperado: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx';
        return;
      }
      
      this.configuredClientUuid = uuid;
      this.uuidInput = '';
      this.uuidError = null;
      
      // Buscar cliente automaticamente após configurar UUID
      this.initializeWidget();
    },

    async initializeWidget() {
      // Busca token de autenticação
      this.userToken = this.getAuthTokenFromExternalSystem();
      
      if (!this.userToken) {
        this.uuidError = 'Token de autenticação não encontrado. Faça login no sistema.';
        this.configuredClientUuid = null;
        return;
      }
      
      // Busca email do usuário
      this.userEmail = this.getUserEmailFromExternalSystem();
      
      // Busca cliente pelo UUID
      this.client = await this.fetchClientByUuid();
      
      if (!this.client) {
        this.uuidError = 'Cliente não encontrado. Verifique se o UUID está correto.';
        this.configuredClientUuid = null;
        return;
      }
    },

    resetUuid() {
      this.configuredClientUuid = null;
      this.client = null;
      this.userToken = null;
      this.userEmail = null;
      this.uuidInput = '';
      this.uuidError = null;
      this.errorMessage = '';
    },
    
    // Métodos para manipulação de cores
    hexToRgba(hex, alpha = 1) {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    
    darkenColor(hex, amount) {
      const num = parseInt(hex.replace('#', ''), 16);
      const amt = Math.round(2.55 * amount * 100);
      const R = (num >> 16) - amt;
      const G = (num >> 8 & 0x00FF) - amt;
      const B = (num & 0x0000FF) - amt;
      return '#' + (0x1000000 + (R < 0 ? 0 : R) * 0x10000 + (G < 0 ? 0 : G) * 0x100 + (B < 0 ? 0 : B)).toString(16).slice(1);
    }
  }
}
</script>

<style>
.ticket-widget {
  position: relative;
}

.ticket-widget .popup-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.ticket-widget .popup-content {
  background: white;
  border-radius: 8px;
  padding: 0;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.ticket-widget .popup-header {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 15px 20px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
  border-radius: 8px 8px 0 0;
}

.ticket-widget .content-header {
  text-align: center;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
}

.ticket-widget .content-header h3 {
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
}

.ticket-widget .content-header p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

.ticket-widget .close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.ticket-widget .close-btn:hover {
  background-color: #f3f4f6;
  color: #374151;
}

.ticket-widget .ticket-form {
  padding: 24px;
}

.ticket-widget .form-group {
  margin-bottom: 20px;
}

.ticket-widget .form-group label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
}

.ticket-widget .form-input,
.ticket-widget .form-textarea,
.ticket-widget .form-select {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.ticket-widget .form-input:focus,
.ticket-widget .form-textarea:focus,
.ticket-widget .form-select:focus {
  outline: none;
  border-color: var(--brand-color, #3b82f6);
  box-shadow: 0 0 0 3px var(--brand-color-focus, rgba(59, 130, 246, 0.1));
}

.ticket-widget .form-textarea {
  resize: vertical;
  min-height: 80px;
}

.ticket-widget .form-select {
  background-color: white;
  cursor: pointer;
}

.ticket-widget .error-message {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 16px;
}

.ticket-widget .form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}

.ticket-widget .btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 100px;
}

.ticket-widget .btn-cancel {
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.ticket-widget .btn-cancel:hover {
  background-color: #e5e7eb;
}

.ticket-widget .btn-submit {
  color: white;
  /* A cor será definida via style inline baseada no brandColor */
}

.ticket-widget .btn-submit:hover:not(:disabled) {
  filter: brightness(0.9);
}

.ticket-widget .btn-submit:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

/* Responsividade */
@media (max-width: 640px) {
  .ticket-widget .popup-content {
    width: 95%;
    margin: 20px;
  }
  
  .ticket-widget .popup-header,
  .ticket-widget .ticket-form {
    padding: 16px;
  }
  
  .ticket-widget .form-actions {
    flex-direction: column;
  }
  
  .ticket-widget .btn {
    width: 100%;
  }
}

/* Estilos para configuração do UUID */
.ticket-widget .uuid-config-section {
  padding: 24px;
}

.ticket-widget .config-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.ticket-widget .config-icon {
  font-size: 20px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0ea5e9;
  border-radius: 50%;
}

.ticket-widget .config-details {
  flex: 1;
}

.ticket-widget .config-title {
  font-size: 14px;
  color: #0369a1;
  font-weight: 500;
}

.ticket-widget .config-subtitle {
  font-size: 13px;
  color: #6b7280;
}

.ticket-widget .form-input.uuid-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

.ticket-widget .form-input.uuid-input:focus {
  outline: none;
  border-color: var(--brand-color, #3b82f6);
  box-shadow: 0 0 0 3px var(--brand-color-focus, rgba(59, 130, 246, 0.1));
}
</style> 